import * as yup from 'yup';
import { RECORDING_TYPE_VALUES } from '../types/recording';
import { AI_PROVIDERS } from '../types/keys';
/**
 * Validate data using Yup schema
 */
export async function validateSchema(schema, data) {
    try {
        const validatedData = await schema.validate(data, { abortEarly: false });
        return {
            success: true,
            data: validatedData,
        };
    }
    catch (error) {
        if (error instanceof yup.ValidationError) {
            return {
                success: false,
                error: {
                    errors: error.inner.map((err) => ({
                        path: err.path || '',
                        message: err.message,
                    })),
                },
            };
        }
        return {
            success: false,
            error: {
                errors: [{
                        path: '',
                        message: 'Validation failed',
                    }],
            },
        };
    }
}
/**
 * Synchronous version of validateSchema for simple use cases
 */
export function validateSchemaSync(schema, data) {
    try {
        const validatedData = schema.validateSync(data, { abortEarly: false });
        return {
            success: true,
            data: validatedData,
        };
    }
    catch (error) {
        if (error instanceof yup.ValidationError) {
            return {
                success: false,
                error: {
                    errors: error.inner.map((err) => ({
                        path: err.path || '',
                        message: err.message,
                    })),
                },
            };
        }
        return {
            success: false,
            error: {
                errors: [{
                        path: '',
                        message: 'Validation failed',
                    }],
            },
        };
    }
}
// ==============================================================================
// VALIDATION SCHEMAS
// ==============================================================================
// Base validation schemas
export const emailSchema = yup
    .string()
    .email('Invalid email format')
    .required('Email is required');
export const passwordSchema = yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number')
    .required('Password is required');
// Auth validation schemas
export const registerSchema = yup.object({
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: yup
        .string()
        .oneOf([yup.ref('password')], 'Passwords must match')
        .required('Confirm password is required'),
    firstName: yup
        .string()
        .min(1, 'First name is required')
        .max(100, 'First name must be less than 100 characters')
        .required('First name is required'),
    lastName: yup
        .string()
        .min(1, 'Last name is required')
        .max(100, 'Last name must be less than 100 characters')
        .required('Last name is required'),
    role: yup
        .string()
        .oneOf(['user', 'superuser'], 'Role must be either user or superuser')
        .optional()
        .default('user'),
});
export const loginSchema = yup.object({
    email: emailSchema,
    password: yup.string().required('Password is required'),
});
export const verifyEmailSchema = yup.object({
    token: yup.string().required('Verification token is required'),
});
export const forgotPasswordSchema = yup.object({
    email: emailSchema,
});
export const resetPasswordSchema = yup.object({
    token: yup.string().required('Reset token is required'),
    newPassword: passwordSchema,
});
export const updatePasswordSchema = yup.object({
    currentPassword: yup.string().required('Current password is required'),
    newPassword: passwordSchema,
});
export const updateProfileSchema = yup.object({
    firstName: yup
        .string()
        .min(1, 'First name is required')
        .max(100, 'First name must be less than 100 characters')
        .required('First name is required'),
    lastName: yup
        .string()
        .min(1, 'Last name is required')
        .max(100, 'Last name must be less than 100 characters')
        .required('Last name is required'),
    email: emailSchema.optional(),
});
export const refreshTokenSchema = yup.object({
    refreshToken: yup.string().required('Refresh token is required'),
});
// Billing validation schemas
export const billingInfoSchema = yup.object({
    subscriptionTier: yup
        .string()
        .oneOf(['free', 'starter', 'team', 'full'], 'Subscription tier must be free, starter, team, or full')
        .required('Subscription tier is required'),
    stripeCustomerId: yup
        .string()
        .matches(/^cus_/, 'Invalid Stripe customer ID')
        .optional(),
    stripeSubscriptionId: yup
        .string()
        .matches(/^sub_/, 'Invalid Stripe subscription ID')
        .optional(),
    teamId: yup
        .string()
        .uuid('Team ID must be a valid UUID')
        .when('subscriptionTier', {
        is: (tier) => ['team', 'full'].includes(tier),
        then: (schema) => schema.required('Team ID is required for team-tier subscriptions'),
        otherwise: (schema) => schema.optional(),
    }),
    subscriptionStatus: yup
        .string()
        .oneOf(['active', 'past_due', 'canceled', 'incomplete', 'trialing'], 'Invalid subscription status')
        .optional(),
    currentPeriodStart: yup
        .string()
        .datetime('Invalid date format')
        .optional(),
    currentPeriodEnd: yup
        .string()
        .datetime('Invalid date format')
        .optional(),
    cancelAtPeriodEnd: yup
        .boolean()
        .optional(),
    trialEnd: yup
        .string()
        .datetime('Invalid date format')
        .nullable()
        .optional(),
}).nullable();
export const updateBillingInfoSchema = yup.object({
    billingInfo: billingInfoSchema,
});
// Session validation schemas
export const createSessionSchema = yup.object({
    title: yup
        .string()
        .min(1, 'Title is required')
        .max(255, 'Title must be less than 255 characters')
        .nullable(),
    description: yup
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .nullable(),
    language: yup
        .string()
        .min(1, 'Language is required')
        .max(50, 'Language must be less than 50 characters')
        .required('Language is required'),
    templateId: yup
        .string()
        .uuid('Template ID must be a valid UUID')
        .required('Template is required'),
    positionId: yup
        .string()
        .uuid('Position ID must be a valid UUID')
        .required('Position is required'),
    dueAt: yup
        .string()
        .datetime('Invalid date format')
        .test('future-date', 'Due time must be in the future', function (value) {
        if (!value)
            return true; // nullable
        const date = new Date(value);
        return date > new Date();
    })
        .nullable(),
    // Optional team session fields
    teamId: yup
        .string()
        .uuid('Team ID must be a valid UUID')
        .optional(),
    // Candidate - required
    candidate: yup.object({
        name: yup.string().min(1, 'Candidate name is required').max(255, 'Name too long').required('Candidate name is required'),
        email: emailSchema.required('Candidate email is required'),
    }).required('Candidate information is required'),
});
export const updateSessionSchema = yup.object({
    title: yup
        .string()
        .min(1, 'Title is required')
        .max(255, 'Title must be less than 255 characters')
        .optional(),
    description: yup
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .nullable()
        .optional(),
    language: yup
        .string()
        .min(1, 'Language is required')
        .max(50, 'Language must be less than 50 characters')
        .optional(),
    templateId: yup
        .string()
        .uuid('Template ID must be a valid UUID')
        .nullable()
        .optional(),
    positionId: yup
        .string()
        .uuid('Position ID must be a valid UUID')
        .nullable()
        .optional(),
    status: yup
        .string()
        .oneOf(['scheduled', 'active', 'completed', 'cancelled'])
        .optional(),
    dueAt: yup
        .string()
        .datetime('Invalid date format')
        .nullable()
        .optional(),
});
export const updateSessionParticipantsSchema = yup.object({
    candidate: yup.object({
        name: yup.string().min(1, 'Candidate name is required').max(255, 'Name too long').required('Candidate name is required'),
        email: emailSchema.required('Candidate email is required'),
    }).optional(),
});
// Team validation schemas
export const createTeamSchema = yup.object({
    name: yup
        .string()
        .min(1, 'Team name is required')
        .max(255, 'Team name must be less than 255 characters')
        .required('Team name is required'),
    slug: yup
        .string()
        .min(1, 'Team slug is required')
        .max(255, 'Team slug must be less than 255 characters')
        .matches(/^[a-z0-9-]+$/, 'Team slug can only contain lowercase letters, numbers, and hyphens')
        .required('Team slug is required'),
    description: yup
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .nullable(),
});
export const updateTeamSchema = yup.object({
    name: yup
        .string()
        .min(1, 'Team name is required')
        .max(255, 'Team name must be less than 255 characters')
        .optional(),
    description: yup
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .nullable()
        .optional(),
    settings: yup
        .object()
        .nullable()
        .optional(),
});
export const inviteTeamMemberSchema = yup.object({
    email: emailSchema,
    role: yup
        .string()
        .oneOf(['admin', 'member'], 'Role must be admin or member')
        .required('Role is required'),
});
// Interview template validation schemas
export const createTemplateSchema = yup.object({
    name: yup
        .string()
        .min(1, 'Template name is required')
        .max(255, 'Template name must be less than 255 characters')
        .required('Template name is required'),
    description: yup
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .nullable(),
    language: yup
        .string()
        .min(1, 'Language is required')
        .max(50, 'Language must be less than 50 characters')
        .required('Language is required'),
    difficulty: yup
        .string()
        .oneOf(['junior', 'mid', 'senior'], 'Difficulty must be junior, mid, or senior')
        .required('Difficulty is required'),
    estimatedDuration: yup
        .number()
        .min(1, 'Estimated duration must be at least 1 minute')
        .max(480, 'Estimated duration cannot exceed 8 hours')
        .required('Estimated duration is required'),
    instructions: yup
        .string()
        .max(10000, 'Instructions must be less than 10000 characters')
        .nullable(),
    starterCode: yup
        .string()
        .max(50000, 'Starter code must be less than 50000 characters')
        .nullable(),
    tags: yup
        .array()
        .of(yup.string().max(50, 'Each tag must be less than 50 characters'))
        .max(10, 'Cannot have more than 10 tags')
        .nullable(),
    isPublic: yup
        .boolean()
        .default(false),
});
export const updateTemplateSchema = yup.object({
    name: yup
        .string()
        .min(1, 'Template name is required')
        .max(255, 'Template name must be less than 255 characters')
        .optional(),
    description: yup
        .string()
        .max(2000, 'Description must be less than 2000 characters')
        .nullable()
        .optional(),
    language: yup
        .string()
        .min(1, 'Language is required')
        .max(50, 'Language must be less than 50 characters')
        .optional(),
    difficulty: yup
        .string()
        .oneOf(['junior', 'mid', 'senior'], 'Difficulty must be junior, mid, or senior')
        .optional(),
    estimatedDuration: yup
        .number()
        .min(1, 'Estimated duration must be at least 1 minute')
        .max(480, 'Estimated duration cannot exceed 8 hours')
        .optional(),
    instructions: yup
        .string()
        .max(10000, 'Instructions must be less than 10000 characters')
        .nullable()
        .optional(),
    starterCode: yup
        .string()
        .max(50000, 'Starter code must be less than 50000 characters')
        .nullable()
        .optional(),
    tags: yup
        .array()
        .of(yup.string().max(50, 'Each tag must be less than 50 characters'))
        .max(10, 'Cannot have more than 10 tags')
        .nullable()
        .optional(),
    isPublic: yup
        .boolean()
        .optional(),
});
// Session participant validation schemas
export const addParticipantSchema = yup.object({
    candidateName: yup
        .string()
        .min(1, 'Candidate name is required when no user ID provided')
        .max(255, 'Candidate name must be less than 255 characters')
        .nullable(),
    candidateEmail: yup
        .string()
        .email('Invalid email format')
        .nullable(),
    // role removed; only candidate exists in async model
});
export const inviteParticipantSchema = yup.object({
    email: emailSchema,
    candidateName: yup
        .string()
        .min(1, 'Candidate name is required')
        .max(255, 'Candidate name must be less than 255 characters')
        .nullable(),
    // role removed; invite is for candidate access only
});
// Common validation schemas
export const uuidSchema = yup
    .string()
    .uuid('Must be a valid UUID')
    .required('ID is required');
export const paginationSchema = yup.object({
    page: yup
        .number()
        .min(1, 'Page must be at least 1')
        .default(1),
    limit: yup
        .number()
        .min(1, 'Limit must be at least 1')
        .max(100, 'Limit cannot exceed 100')
        .default(20),
    search: yup
        .string()
        .max(255, 'Search term must be less than 255 characters')
        .nullable(),
    sortBy: yup
        .string()
        .max(50, 'Sort field must be less than 50 characters')
        .nullable(),
    sortOrder: yup
        .string()
        .oneOf(['asc', 'desc'], 'Sort order must be asc or desc')
        .default('desc'),
});
// Subscription validation schemas
export const subscribeToTierSchema = yup.object({
    subscriptionTier: yup
        .string()
        .oneOf(['free', 'starter', 'team', 'full'], 'Invalid subscription tier')
        .required('Subscription tier is required'),
    teamData: yup.object({
        name: yup
            .string()
            .min(1, 'Team name is required')
            .max(255, 'Team name must be less than 255 characters')
            .required('Team name is required'),
        slug: yup
            .string()
            .min(3, 'Team slug must be at least 3 characters')
            .max(255, 'Team slug must be less than 255 characters')
            .matches(/^[a-z0-9-]+$/, 'Team slug can only contain lowercase letters, numbers, and hyphens')
            .required('Team slug is required'),
        description: yup
            .string()
            .max(2000, 'Description must be less than 2000 characters')
            .optional(),
    }).when('subscriptionTier', {
        is: (tier) => ['team', 'full'].includes(tier),
        then: (schema) => schema.required('Team data is required for team subscriptions'),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
});
// Candidate session validation schemas
export const joinSessionWithInviteSchema = yup.object({
    inviteToken: yup.string().required('Invite token is required'),
});
export const createAssessmentSchema = yup.object({
    scores: yup.object({
        aiLiteracy: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
        codeOrganization: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
        problemUnderstanding: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
        solutionQuality: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
        communication: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
        creativity: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
        efficiency: yup.number().nullable().min(1, 'Score must be between 1 and 10').max(10, 'Score must be between 1 and 10'),
    }).required('Scores are required'),
    notes: yup.object({
        aiLiteracy: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
        codeOrganization: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
        problemUnderstanding: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
        solutionQuality: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
        communication: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
        creativity: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
        efficiency: yup.string().max(1000, 'Note must be less than 1000 characters').nullable().optional(),
    }).nullable().optional(),
    overallNote: yup.string().max(2000, 'Overall note must be less than 2000 characters').nullable().optional(),
});
export const joinSessionWithCodeSchema = yup.object({
    sessionCode: yup.string().required('Session code is required'),
    candidateName: yup
        .string()
        .max(255, 'Candidate name must be less than 255 characters')
        .optional(),
    candidateEmail: yup
        .string()
        .email('Must be a valid email')
        .max(255, 'Email must be less than 255 characters')
        .optional(),
});
export const sessionFileInputSchema = yup.object({
    clientFileId: yup.string().optional(),
    stepId: yup.string().uuid('Step ID must be a valid UUID').optional(),
    filename: yup
        .string()
        .min(1, 'Filename is required')
        .max(255, 'Filename must be less than 255 characters')
        .required('Filename is required'),
    content: yup
        .string()
        .max(10 * 1024 * 1024, 'File content must be less than 10MB') // 10MB limit
        .defined(), // allow empty string content
    createdAt: yup
        .string()
        .optional(),
});
export const batchSessionFilesSchema = yup.object({
    files: yup
        .array()
        .of(sessionFileInputSchema)
        .min(1, 'At least one file is required')
        .max(20, 'Cannot upload more than 20 files at once')
        .required('Files array is required'),
    idempotencyKey: yup
        .string()
        .max(255, 'Idempotency key must be less than 255 characters')
        .optional(),
});
export const sessionRecordingInputSchema = yup.object({
    type: yup
        .string()
        .oneOf(RECORDING_TYPE_VALUES, 'Invalid recording type')
        .required('Recording type is required'),
    timestamp: yup
        .string()
        .required('Timestamp is required'),
    stepId: yup.string().uuid('Step ID must be a valid UUID').optional(),
}).concat(
// Add specific validation based on recording type using 'when'
yup.object().when('type', {
    is: 'code_change',
    then: (schema) => schema.shape({
        filename: yup.string().required('Filename is required for code change'),
        size: yup.number().required('Size is required for code change'),
        content: yup.string().required('Content is required for code change'),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'file_switch',
    then: (schema) => schema.shape({
        from: yup.string().required('From filename is required for file switch'),
        to: yup.string().required('To filename is required for file switch'),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'file_rename',
    then: (schema) => schema.shape({
        before: yup.string().required('Before filename is required for file rename'),
        after: yup.string().required('After filename is required for file rename'),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'file_delete',
    then: (schema) => schema.shape({
        filename: yup.string().required('Filename is required for file delete'),
        id: yup.string().optional(),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'step_transition',
    then: (schema) => schema.shape({
        toIndex: yup.number().required('To index is required for step transition'),
        toStepId: yup.string().required('To step ID is required for step transition'),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'ai_request',
    then: (schema) => schema.shape({
        prompt: yup.string().required('Prompt is required for AI request'),
        response: yup.string().required('Response is required for AI request'),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'cursor_move',
    then: (schema) => schema.shape({
        filename: yup.string().required('Filename is required for cursor move'),
        position: yup.object().shape({
            line: yup.number().required('Line is required for cursor position'),
            column: yup.number().required('Column is required for cursor position'),
        }).required('Position is required for cursor move'),
    }),
    otherwise: (schema) => schema,
}).when('type', {
    is: 'chat_message',
    then: (schema) => schema.shape({
        message: yup.string().required('Message is required for chat message'),
        role: yup.string().oneOf(['user', 'assistant'], 'Role must be user or assistant').required('Role is required for chat message'),
    }),
    otherwise: (schema) => schema,
}));
export const batchSessionRecordingsSchema = yup.object({
    recordings: yup
        .array()
        .of(sessionRecordingInputSchema)
        .min(1, 'At least one recording is required')
        .max(100, 'Cannot submit more than 100 recordings at once')
        .required('Recordings array is required'),
    idempotencyKey: yup
        .string()
        .max(255, 'Idempotency key must be less than 255 characters')
        .optional(),
});
export const aiInteractionInputSchema = yup.object({
    prompt: yup
        .string()
        .min(1, 'Prompt is required')
        .max(10000, 'Prompt must be less than 10000 characters')
        .required('Prompt is required'),
    response: yup
        .string()
        .min(1, 'Response is required')
        .max(50000, 'Response must be less than 50000 characters')
        .required('Response is required'),
    tokensUsed: yup
        .number()
        .min(0, 'Tokens used must be non-negative')
        .integer('Tokens used must be an integer')
        .required('Tokens used is required'),
    metadata: yup
        .object()
        .optional(),
    timestamp: yup
        .string()
        .required('Timestamp is required'),
});
export const endSessionSchema = yup.object({
// Currently no additional fields needed
});
// Support ticket validation
export const supportAttachmentSchema = yup.object({
    filename: yup
        .string()
        .min(1, 'Filename is required')
        .max(255, 'Filename must be less than 255 characters')
        .required('Filename is required'),
    url: yup
        .string()
        .url('Invalid URL')
        .required('URL is required'),
    size: yup
        .number()
        .min(0, 'Size must be non-negative')
        .optional(),
}).optional();
export const createSupportTicketSchema = yup.object({
    subject: yup
        .string()
        .min(1, 'Subject is required')
        .max(255, 'Subject must be less than 255 characters')
        .required('Subject is required'),
    description: yup
        .string()
        .max(20000, 'Description is too long')
        .nullable(),
    requesterEmail: yup
        .string()
        .email('Invalid email format')
        .nullable(),
    priority: yup
        .string()
        .oneOf(['low', 'normal', 'high', 'urgent'], 'Invalid priority')
        .optional()
        .default('normal'),
    teamUuid: yup
        .string()
        .uuid('Team UUID must be a valid UUID')
        .nullable(),
    attachments: yup
        .array()
        .of(supportAttachmentSchema)
        .nullable(),
    metadata: yup
        .object()
        .nullable(),
    requesterUuid: yup
        .string()
        .uuid('Requester UUID must be a valid UUID')
        .nullable(),
}).required();
export const upsertUserApiKeySchema = yup.object({
    provider: yup.mixed().oneOf(AI_PROVIDERS, 'Invalid provider').required('Provider is required'),
    apiKey: yup.string().min(10, 'API key seems too short').max(200, 'API key too long').required('API key is required'),
});
// Position validation schemas
export const createPositionSchema = yup.object({
    name: yup.string().min(1, 'Name is required').max(120, 'Name must be less than 120 characters').required('Name is required'),
    description: yup.string().max(1000, 'Description must be less than 1000 characters').optional(),
    teamId: yup.string().uuid('Team ID must be a valid UUID').optional(),
    hiringEndDate: yup.string().datetime('Invalid date format').optional(),
});
export const updatePositionSchema = yup.object({
    name: yup.string().min(1, 'Name is required').max(120, 'Name must be less than 120 characters').optional(),
    description: yup.string().max(1000, 'Description must be less than 1000 characters').nullable().optional(),
    hiringEndDate: yup.string().datetime('Invalid date format').nullable().optional(),
});
